import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, LogOut, Eye, EyeOff, Upload, Loader2, Sparkles, BarChart3, Trophy } from "lucide-react";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import WriterStats from "@/components/WriterStats";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState("");
  const [userRole, setUserRole] = useState("admin");
  const [writerName, setWriterName] = useState(null);
  const [articles, setArticles] = useState([]);
  const [writers, setWriters] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editingWriter, setEditingWriter] = useState(null);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isWriterDialogOpen, setIsWriterDialogOpen] = useState(false);
  const [isAchievementDialogOpen, setIsAchievementDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [uploading, setUploading] = useState(false);
  const [uploadingWriterImage, setUploadingWriterImage] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [selectedWriterForStats, setSelectedWriterForStats] = useState(null);
  const fileInputRef = useRef(null);
  const writerFileInputRef = useRef(null);

  // Login form
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });

  // Article form
  const [articleForm, setArticleForm] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "News",
    image_url: "",
    image_credit: "",
    author: "Filmcable Editorial"
  });

  // Writer form
  const [writerForm, setWriterForm] = useState({
    name: "",
    bio: "",
    profile_image: "",
    instagram: "",
    twitter: "",
    username: "",
    password: "",
    login_enabled: false
  });

  // Achievement form
  const [achievementForm, setAchievementForm] = useState({
    title: "",
    description: "",
    date: new Date().toISOString().split('T')[0],
    link: ""
  });

  const categories = ["Film", "Series", "Industry", "Awards", "Festivals", "Reviews", "Suggestions", "Stories"];

  // Check if article can be edited/deleted by writer (within 10 minutes)
  const canWriterModify = (article) => {
    if (userRole === "admin") return true;
    if (!article.created_at) return false;
    const createdTime = new Date(article.created_at);
    const now = new Date();
    const diffMinutes = (now - createdTime) / (1000 * 60);
    return diffMinutes < 10;
  };

  useEffect(() => {
    const savedToken = localStorage.getItem("filmcable_token");
    if (savedToken) {
      verifyToken(savedToken);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${tokenToVerify}` }
      });
      setToken(tokenToVerify);
      setIsAuthenticated(true);
      setUserRole(response.data.role || "admin");
      setWriterName(response.data.writer_name || null);
      fetchArticles(tokenToVerify);
      if (response.data.role === "admin") {
        fetchWriters(tokenToVerify);
        fetchAchievements(tokenToVerify);
        setActiveTab("dashboard");
      } else {
        setActiveTab("articles");
      }
    } catch {
      localStorage.removeItem("filmcable_token");
      setIsAuthenticated(false);
    }
  };

  const fetchArticles = async (authToken) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/articles/all`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setArticles(response.data);
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWriters = async (authToken) => {
    try {
      const response = await axios.get(`${API}/writers`);
      setWriters(response.data);
    } catch (error) {
      console.error("Error fetching writers:", error);
    }
  };

  const fetchAchievements = async (authToken) => {
    try {
      const response = await axios.get(`${API}/achievements?active_only=false`);
      setAchievements(response.data);
    } catch (error) {
      console.error("Error fetching achievements:", error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, loginForm);
      const { token: newToken, role, writer_name } = response.data;
      localStorage.setItem("filmcable_token", newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      setUserRole(role || "admin");
      setWriterName(writer_name || null);
      fetchArticles(newToken);
      if (role === "admin") {
        fetchWriters(newToken);
        fetchAchievements(newToken);
        setActiveTab("dashboard");
      } else {
        setActiveTab("articles");
      }
      toast.success("Logged in successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("filmcable_token");
    setToken("");
    setIsAuthenticated(false);
    setUserRole("admin");
    setWriterName(null);
    setArticles([]);
    setWriters([]);
    setAchievements([]);
    setActiveTab("dashboard");
    toast.success("Logged out");
  };

  const handleSaveArticle = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.id}`, articleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/articles`, articleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Article created");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchArticles(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save article");
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    
    try {
      await axios.delete(`${API}/articles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Article deleted");
      fetchArticles(token);
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const handleToggleStatus = async (article) => {
    const newStatus = article.status === "published" ? "draft" : "published";
    try {
      await axios.put(`${API}/articles/${article.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Article ${newStatus}`);
      fetchArticles(token);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setArticleForm({ ...articleForm, image_url: response.data.url });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!articleForm.title.trim()) {
      toast.error('Please enter a title first');
      return;
    }

    setGeneratingImage(true);
    toast.info('Generating image... This may take 15-30 seconds');

    try {
      const response = await axios.post(`${API}/generate-image`, {
        title: articleForm.title,
        content: articleForm.content || articleForm.excerpt || ''
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000 // 2 minute timeout for image generation
      });
      
      setArticleForm({ ...articleForm, image_url: response.data.url });
      toast.success('Image generated successfully!');
    } catch (error) {
      console.error('Image generation error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to generate image. Please try again.';
      toast.error(errorMessage);
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleWriterImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploadingWriterImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setWriterForm({ ...writerForm, profile_image: response.data.url });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingWriterImage(false);
    }
  };

  // Writer CRUD
  const handleSaveWriter = async (e) => {
    e.preventDefault();
    
    // Frontend validation: if login_enabled, require username and password
    if (writerForm.login_enabled) {
      if (!writerForm.username || !writerForm.username.trim()) {
        toast.error("Username is required when login is enabled");
        return;
      }
      if (!editingWriter && !writerForm.password) {
        toast.error("Password is required when enabling login for a new writer");
        return;
      }
    }
    
    try {
      // Prepare form data - only include password if it's set
      const formData = { ...writerForm };
      if (!formData.password) {
        delete formData.password; // Don't send empty password
      }
      
      if (editingWriter) {
        await axios.put(`${API}/writers/${editingWriter.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Writer updated");
      } else {
        await axios.post(`${API}/writers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Writer created");
      }
      setIsWriterDialogOpen(false);
      resetWriterForm();
      fetchWriters(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save writer");
    }
  };

  const handleDeleteWriter = async (id) => {
    if (!window.confirm("Are you sure you want to delete this writer?")) return;
    
    try {
      await axios.delete(`${API}/writers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Writer deleted");
      fetchWriters(token);
    } catch (error) {
      toast.error("Failed to delete writer");
    }
  };

  const openEditWriterDialog = (writer) => {
    setEditingWriter(writer);
    setWriterForm({
      name: writer.name,
      bio: writer.bio || "",
      profile_image: writer.profile_image || "",
      instagram: writer.instagram || "",
      twitter: writer.twitter || "",
      username: writer.username || "",
      password: "", // Don't prefill password
      login_enabled: writer.login_enabled || false
    });
    setIsWriterDialogOpen(true);
  };

  const resetWriterForm = () => {
    setEditingWriter(null);
    setWriterForm({
      name: "",
      bio: "",
      profile_image: "",
      instagram: "",
      twitter: "",
      username: "",
      password: "",
      login_enabled: false
    });
  };

  // Achievement CRUD
  const handleSaveAchievement = async (e) => {
    e.preventDefault();
    try {
      if (editingAchievement) {
        await axios.put(`${API}/achievements/${editingAchievement.id}`, achievementForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Achievement updated");
      } else {
        await axios.post(`${API}/achievements`, achievementForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Achievement created");
      }
      setIsAchievementDialogOpen(false);
      resetAchievementForm();
      fetchAchievements(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save achievement");
    }
  };

  const handleDeleteAchievement = async (id) => {
    if (!window.confirm("Are you sure you want to delete this achievement?")) return;
    
    try {
      await axios.delete(`${API}/achievements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Achievement deleted");
      fetchAchievements(token);
    } catch (error) {
      toast.error("Failed to delete achievement");
    }
  };

  const openEditAchievementDialog = (achievement) => {
    setEditingAchievement(achievement);
    setAchievementForm({
      title: achievement.title,
      description: achievement.description,
      date: achievement.date,
      link: achievement.link || ""
    });
    setIsAchievementDialogOpen(true);
  };

  const resetAchievementForm = () => {
    setEditingAchievement(null);
    setAchievementForm({
      title: "",
      description: "",
      date: new Date().toISOString().split('T')[0],
      link: ""
    });
  };

  const openEditDialog = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      category: article.category,
      image_url: article.image_url,
      image_credit: article.image_credit || "",
      author: article.author
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingArticle(null);
    setArticleForm({
      title: "",
      excerpt: "",
      content: "",
      category: "News",
      image_url: "",
      image_credit: "",
      author: "Filmcable Editorial"
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50" data-testid="admin-login-page">
        <div className="w-full max-w-md p-8 bg-white border border-zinc-200">
          <h1 className="font-serif text-3xl font-bold mb-2 text-center">
            Filmcable Admin
          </h1>
          <p className="text-zinc-500 text-center mb-8">
            Sign in to manage content
          </p>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                required
                className="rounded-none"
                data-testid="username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                required
                className="rounded-none"
                data-testid="password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full rounded-none bg-[#FF66C4] hover:bg-[#e55ab0] font-bold uppercase tracking-wider"
              data-testid="login-btn"
            >
              Sign In
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 py-4">
        <div className="container flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl font-bold">Content Manager</h1>
            {userRole === "writer" && writerName && (
              <p className="text-sm text-zinc-500">Logged in as {writerName}</p>
            )}
          </div>
          <div className="flex items-center gap-4">
            {userRole === "writer" && (
              <span className="px-3 py-1 bg-[#FF66C4]/10 text-[#FF66C4] text-xs font-bold uppercase rounded">
                Writer
              </span>
            )}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-zinc-600 hover:text-zinc-900"
              data-testid="logout-btn"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-8">
        {/* Tabs - Dashboard, Achievements, Writers tabs only visible to admins */}
        <div className="flex gap-4 mb-8 border-b border-zinc-200 overflow-x-auto">
          {userRole === "admin" && (
            <button
              onClick={() => setActiveTab("dashboard")}
              data-testid="dashboard-tab"
              className={`pb-4 font-bold uppercase tracking-wider text-sm border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === "dashboard" 
                  ? "border-[#FF66C4] text-[#FF66C4]" 
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              <BarChart3 size={16} />
              Dashboard
            </button>
          )}
          <button
            onClick={() => setActiveTab("articles")}
            data-testid="articles-tab"
            className={`pb-4 font-bold uppercase tracking-wider text-sm border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "articles" 
                ? "border-[#FF66C4] text-[#FF66C4]" 
                : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            {userRole === "writer" ? "My Articles" : "Articles"} ({articles.length})
          </button>
          {userRole === "admin" && (
            <button
              onClick={() => setActiveTab("writers")}
              data-testid="writers-tab"
              className={`pb-4 font-bold uppercase tracking-wider text-sm border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "writers" 
                  ? "border-[#FF66C4] text-[#FF66C4]" 
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              Writers ({writers.length})
            </button>
          )}
          {userRole === "admin" && (
            <button
              onClick={() => setActiveTab("achievements")}
              className={`pb-4 font-bold uppercase tracking-wider text-sm border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === "achievements" 
                  ? "border-[#FF66C4] text-[#FF66C4]" 
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              <Trophy size={16} />
              Achievements ({achievements.length})
            </button>
          )}
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && userRole === "admin" && (
          <AnalyticsDashboard token={token} />
        )}

        {/* Articles Tab */}
        {activeTab === "articles" && (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-serif text-xl font-bold">{userRole === "writer" ? "My Articles" : "Articles"}</h2>
                <p className="text-zinc-500">
                  {articles.length} {userRole === "writer" ? "articles by you" : "total articles"}
                </p>
              </div>
              
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    onClick={resetForm}
                    className="rounded-none bg-[#FF66C4] hover:bg-[#e55ab0] font-bold uppercase tracking-wider"
                    data-testid="new-article-btn"
                  >
                    <Plus size={18} className="mr-2" />
                    New Article
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                      {editingArticle ? "Edit Article" : "New Article"}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleSaveArticle} className="space-y-6 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        value={articleForm.title}
                        onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                        required
                        className="rounded-none"
                        data-testid="article-title-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="excerpt">Excerpt</Label>
                      <Textarea
                        id="excerpt"
                        value={articleForm.excerpt}
                        onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })}
                        required
                        className="rounded-none"
                        rows={2}
                        data-testid="article-excerpt-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="content">Content</Label>
                      <Textarea
                        id="content"
                        value={articleForm.content}
                        onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                        required
                        className="rounded-none min-h-[200px]"
                        data-testid="article-content-input"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="category">Category</Label>
                        <Select
                          value={articleForm.category}
                          onValueChange={(value) => setArticleForm({ ...articleForm, category: value })}
                        >
                          <SelectTrigger className="rounded-none" data-testid="article-category-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Author dropdown - only shown to admins */}
                      {userRole === "admin" ? (
                        <div className="space-y-2">
                          <Label htmlFor="author">Author</Label>
                          <Select
                            value={articleForm.author}
                            onValueChange={(value) => setArticleForm({ ...articleForm, author: value })}
                          >
                            <SelectTrigger className="rounded-none" data-testid="article-author-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Filmcable Editorial">Filmcable Editorial</SelectItem>
                              {writers.map((writer) => (
                                <SelectItem key={writer.id} value={writer.name}>{writer.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label>Author</Label>
                          <div className="px-3 py-2 border rounded-none bg-zinc-50 text-zinc-700">
                            {writerName || "You"}
                          </div>
                          <p className="text-xs text-zinc-400">Articles will be published under your name</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Article Image</Label>
                      
                      {/* Upload Button */}
                      <div className="flex gap-2">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          accept="image/*"
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading || generatingImage}
                          className="rounded-none flex-1"
                        >
                          {uploading ? (
                            <>
                              <Loader2 size={18} className="mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload size={18} className="mr-2" />
                              Upload Image
                            </>
                          )}
                        </Button>
                      </div>

                      {/* AI Generate Button */}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleGenerateImage}
                        disabled={generatingImage || uploading || !articleForm.title.trim()}
                        className="rounded-none w-full border-[#FF66C4] text-[#FF66C4] hover:bg-[#FF66C4] hover:text-white"
                        data-testid="generate-image-btn"
                      >
                        {generatingImage ? (
                          <>
                            <Loader2 size={18} className="mr-2 animate-spin" />
                            Generating AI Image...
                          </>
                        ) : (
                          <>
                            <Sparkles size={18} className="mr-2" />
                            Generate Image with AI
                          </>
                        )}
                      </Button>
                      {!articleForm.title.trim() && (
                        <p className="text-xs text-zinc-400">Enter a title to enable AI image generation</p>
                      )}

                      {/* Or use URL */}
                      <div className="flex items-center gap-2 my-2">
                        <div className="h-px bg-zinc-200 flex-1" />
                        <span className="text-xs text-zinc-400">or paste URL</span>
                        <div className="h-px bg-zinc-200 flex-1" />
                      </div>

                      <Input
                        type="url"
                        value={articleForm.image_url}
                        onChange={(e) => setArticleForm({ ...articleForm, image_url: e.target.value })}
                        className="rounded-none"
                        placeholder="https://..."
                        data-testid="article-image-input"
                      />

                      {/* Image Preview */}
                      {articleForm.image_url && (
                        <div className="relative mt-2">
                          <img
                            src={articleForm.image_url}
                            alt="Preview"
                            className="h-40 w-full object-cover"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2 rounded-none"
                            onClick={() => setArticleForm({ ...articleForm, image_url: '' })}
                          >
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="image_credit">Picture Credits</Label>
                      <Input
                        id="image_credit"
                        value={articleForm.image_credit}
                        onChange={(e) => setArticleForm({ ...articleForm, image_credit: e.target.value })}
                        className="rounded-none"
                        placeholder="e.g., Instagram.com/@photographer or Photo by John Doe"
                        data-testid="article-image-credit-input"
                      />
                      <p className="text-xs text-zinc-500">Credit shown below the article image</p>
                    </div>
                    
                    <div className="flex gap-4 pt-4">
                      <Button
                        type="submit"
                        className="flex-1 rounded-none bg-[#FF66C4] hover:bg-[#e55ab0] font-bold uppercase tracking-wider"
                        data-testid="save-article-btn"
                      >
                        {editingArticle ? "Update Article" : "Publish Article"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsDialogOpen(false)}
                        className="rounded-none"
                        data-testid="cancel-article-btn"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Articles Table */}
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 skeleton" />
                ))}
              </div>
            ) : (
              <div className="bg-white border border-zinc-200">
                <div className="grid grid-cols-12 gap-4 p-4 border-b border-zinc-200 font-bold text-sm uppercase tracking-wider text-zinc-500">
                  <div className="col-span-5">Title</div>
                  <div className="col-span-2">Category</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-3 text-right">Actions</div>
                </div>
                
                {articles.length === 0 ? (
                  <div className="p-8 text-center text-zinc-500">
                    {userRole === "writer" 
                      ? "You haven't published any articles yet. Create your first article!" 
                      : "No articles yet. Create your first article!"}
                  </div>
                ) : (
                  articles.map((article) => {
                    const canModify = canWriterModify(article);
                    const timeRemaining = (() => {
                      if (userRole === "admin") return null;
                      if (!article.created_at) return null;
                      const createdTime = new Date(article.created_at);
                      const now = new Date();
                      const diffMinutes = 10 - ((now - createdTime) / (1000 * 60));
                      if (diffMinutes <= 0) return null;
                      return Math.ceil(diffMinutes);
                    })();
                    
                    return (
                      <div
                        key={article.id}
                        className="grid grid-cols-12 gap-4 p-4 border-b border-zinc-100 items-center hover:bg-zinc-50"
                        data-testid={`article-row-${article.id}`}
                      >
                        <div className="col-span-5">
                          <p className="font-medium truncate">{article.title}</p>
                          <p className="text-sm text-zinc-500">{article.author}</p>
                          {userRole === "writer" && timeRemaining && (
                            <p className="text-xs text-orange-500 mt-1">
                              {timeRemaining} min left to edit/delete
                            </p>
                          )}
                        </div>
                        <div className="col-span-2">
                          <span className="font-accent text-xs font-bold uppercase tracking-widest text-[#FF66C4]">
                            {article.category}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className={`text-sm font-medium ${
                            article.status === "published" ? "text-green-600" : "text-zinc-400"
                          }`}>
                            {article.status}
                          </span>
                        </div>
                        <div className="col-span-3 flex justify-end gap-2">
                          {/* Toggle status - admin only */}
                          {userRole === "admin" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleStatus(article)}
                              title={article.status === "published" ? "Unpublish" : "Publish"}
                              data-testid={`toggle-status-${article.id}`}
                            >
                              {article.status === "published" ? <EyeOff size={18} /> : <Eye size={18} />}
                            </Button>
                          )}
                          {/* Edit button - show for admin always, for writers only if within 10 min */}
                          {(userRole === "admin" || canModify) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(article)}
                              data-testid={`edit-article-${article.id}`}
                              title={userRole === "writer" && !canModify ? "Edit window expired" : "Edit"}
                            >
                              <Edit size={18} />
                            </Button>
                          )}
                          {/* Delete button - show for admin always, for writers only if within 10 min */}
                          {(userRole === "admin" || canModify) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteArticle(article.id)}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`delete-article-${article.id}`}
                              title={userRole === "writer" && !canModify ? "Delete window expired" : "Delete"}
                            >
                              <Trash2 size={18} />
                            </Button>
                          )}
                          {/* Show locked icon for writers when edit window expired */}
                          {userRole === "writer" && !canModify && (
                            <span className="text-zinc-300 px-2" title="Edit/Delete window expired">
                              Locked
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}

        {/* Writers Tab */}
        {activeTab === "writers" && (
          <>
            {selectedWriterForStats ? (
              <WriterStats 
                writer={selectedWriterForStats} 
                token={token} 
                onBack={() => setSelectedWriterForStats(null)} 
              />
            ) : (
            <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-serif text-xl font-bold">Writers</h2>
                <p className="text-zinc-500">{writers.length} writers</p>
              </div>
              
              <Dialog open={isWriterDialogOpen} onOpenChange={setIsWriterDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    onClick={resetWriterForm}
                    className="rounded-none bg-[#FF66C4] hover:bg-[#e55ab0] font-bold uppercase tracking-wider"
                    data-testid="new-writer-btn"
                  >
                    <Plus size={18} className="mr-2" />
                    New Writer
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                      {editingWriter ? "Edit Writer" : "New Writer"}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleSaveWriter} className="space-y-6 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="writer-name">Name</Label>
                      <Input
                        id="writer-name"
                        value={writerForm.name}
                        onChange={(e) => setWriterForm({ ...writerForm, name: e.target.value })}
                        required
                        className="rounded-none"
                        placeholder="Full name"
                        data-testid="writer-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="writer-bio">Bio</Label>
                      <Textarea
                        id="writer-bio"
                        value={writerForm.bio}
                        onChange={(e) => setWriterForm({ ...writerForm, bio: e.target.value })}
                        className="rounded-none"
                        rows={3}
                        placeholder="Short bio about the writer..."
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Profile Image</Label>
                      <div className="flex gap-2">
                        <input
                          type="file"
                          ref={writerFileInputRef}
                          onChange={handleWriterImageUpload}
                          accept="image/*"
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => writerFileInputRef.current?.click()}
                          disabled={uploadingWriterImage}
                          className="rounded-none flex-1"
                        >
                          {uploadingWriterImage ? (
                            <>
                              <Loader2 size={18} className="mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload size={18} className="mr-2" />
                              Upload Photo
                            </>
                          )}
                        </Button>
                      </div>
                      {writerForm.profile_image && (
                        <div className="relative mt-2 w-24 h-24">
                          <img
                            src={writerForm.profile_image}
                            alt="Preview"
                            className="w-full h-full object-cover rounded-full"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute -top-2 -right-2 rounded-full w-6 h-6 p-0"
                            onClick={() => setWriterForm({ ...writerForm, profile_image: '' })}
                          >
                            ×
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="writer-instagram">Instagram</Label>
                        <Input
                          id="writer-instagram"
                          value={writerForm.instagram}
                          onChange={(e) => setWriterForm({ ...writerForm, instagram: e.target.value })}
                          className="rounded-none"
                          placeholder="username (without @)"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="writer-twitter">Twitter</Label>
                        <Input
                          id="writer-twitter"
                          value={writerForm.twitter}
                          onChange={(e) => setWriterForm({ ...writerForm, twitter: e.target.value })}
                          className="rounded-none"
                          placeholder="username (without @)"
                        />
                      </div>
                    </div>
                    
                    {/* Login Credentials Section */}
                    <div className="border-t border-zinc-200 pt-6 mt-6">
                      <h4 className="font-bold text-sm uppercase tracking-wider text-zinc-500 mb-4">
                        Login Credentials (Optional)
                      </h4>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="writer-username">Username</Label>
                            <Input
                              id="writer-username"
                              value={writerForm.username}
                              onChange={(e) => setWriterForm({ ...writerForm, username: e.target.value })}
                              className="rounded-none"
                              placeholder="Login username"
                              data-testid="writer-username-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="writer-password">
                              {editingWriter ? "New Password" : "Password"}
                            </Label>
                            <Input
                              id="writer-password"
                              type="password"
                              value={writerForm.password}
                              onChange={(e) => setWriterForm({ ...writerForm, password: e.target.value })}
                              className="rounded-none"
                              placeholder={editingWriter ? "Leave blank to keep" : "Set password"}
                              data-testid="writer-password-input"
                            />
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id="writer-login-enabled"
                            checked={writerForm.login_enabled}
                            onChange={(e) => setWriterForm({ ...writerForm, login_enabled: e.target.checked })}
                            className="w-4 h-4 accent-[#FF66C4]"
                            data-testid="writer-login-enabled-checkbox"
                          />
                          <Label htmlFor="writer-login-enabled" className="cursor-pointer">
                            Enable login for this writer
                          </Label>
                        </div>
                        
                        {writerForm.username && !writerForm.login_enabled && (
                          <p className="text-xs text-orange-500">
                            Login is disabled. Writer won&apos;t be able to sign in until enabled.
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-4 pt-4">
                      <Button
                        type="submit"
                        className="flex-1 rounded-none bg-[#FF66C4] hover:bg-[#e55ab0] font-bold uppercase tracking-wider"
                        data-testid="save-writer-btn"
                      >
                        {editingWriter ? "Update Writer" : "Add Writer"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsWriterDialogOpen(false)}
                        className="rounded-none"
                        data-testid="cancel-writer-btn"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Writers Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {writers.length === 0 ? (
                <div className="col-span-full p-8 text-center text-zinc-500 bg-white border border-zinc-200">
                  No writers yet. Add your first writer!
                </div>
              ) : (
                writers.map((writer) => (
                  <div
                    key={writer.id}
                    className="bg-white border border-zinc-200 p-6"
                    data-testid={`writer-card-${writer.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {writer.profile_image ? (
                        <img
                          src={writer.profile_image}
                          alt={writer.name}
                          className="w-16 h-16 object-cover rounded-full"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-zinc-200 rounded-full flex items-center justify-center">
                          <span className="font-serif text-xl text-zinc-400">
                            {writer.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-serif text-lg font-bold truncate">{writer.name}</h3>
                        <p className="text-sm text-zinc-500 line-clamp-2">{writer.bio || "No bio"}</p>
                        {writer.username && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              writer.login_enabled 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-zinc-100 text-zinc-500'
                            }`}>
                              {writer.login_enabled ? 'Login enabled' : 'Login disabled'}
                            </span>
                            <span className="text-xs text-zinc-400">@{writer.username}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-zinc-100">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedWriterForStats(writer)}
                        className="text-[#FF66C4] hover:text-[#e55ab0]"
                        data-testid={`writer-stats-${writer.id}`}
                      >
                        <BarChart3 size={16} className="mr-1" />
                        Stats
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditWriterDialog(writer)}
                        data-testid={`edit-writer-${writer.id}`}
                      >
                        <Edit size={16} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteWriter(writer.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-writer-${writer.id}`}
                      >
                        <Trash2 size={16} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
            </>
            )}
          </>
        )}

        {/* Achievements Tab */}
        {activeTab === "achievements" && userRole === "admin" && (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-serif text-xl font-bold">Achievements</h2>
                <p className="text-zinc-500">{achievements.length} milestones</p>
              </div>
              
              <Dialog open={isAchievementDialogOpen} onOpenChange={setIsAchievementDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    onClick={resetAchievementForm}
                    className="rounded-none bg-[#FF66C4] hover:bg-[#e55ab0] font-bold uppercase tracking-wider"
                    data-testid="new-achievement-btn"
                  >
                    <Plus size={18} className="mr-2" />
                    New Achievement
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                      {editingAchievement ? "Edit Achievement" : "New Achievement"}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleSaveAchievement} className="space-y-6 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="achievement-title">Title</Label>
                      <Input
                        id="achievement-title"
                        value={achievementForm.title}
                        onChange={(e) => setAchievementForm({ ...achievementForm, title: e.target.value })}
                        required
                        className="rounded-none"
                        placeholder="e.g., Featured in Variety"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="achievement-description">Description</Label>
                      <Textarea
                        id="achievement-description"
                        value={achievementForm.description}
                        onChange={(e) => setAchievementForm({ ...achievementForm, description: e.target.value })}
                        required
                        className="rounded-none"
                        rows={3}
                        placeholder="Describe this achievement..."
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="achievement-date">Date</Label>
                        <Input
                          id="achievement-date"
                          type="date"
                          value={achievementForm.date}
                          onChange={(e) => setAchievementForm({ ...achievementForm, date: e.target.value })}
                          required
                          className="rounded-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="achievement-link">Link (optional)</Label>
                        <Input
                          id="achievement-link"
                          type="url"
                          value={achievementForm.link}
                          onChange={(e) => setAchievementForm({ ...achievementForm, link: e.target.value })}
                          className="rounded-none"
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-4 pt-4">
                      <Button
                        type="submit"
                        className="flex-1 rounded-none bg-[#FF66C4] hover:bg-[#e55ab0] font-bold uppercase tracking-wider"
                      >
                        {editingAchievement ? "Update Achievement" : "Add Achievement"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsAchievementDialogOpen(false)}
                        className="rounded-none"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Achievements List */}
            <div className="space-y-4">
              {achievements.length === 0 ? (
                <div className="p-8 text-center text-zinc-500 bg-white border border-zinc-200">
                  No achievements yet. Add your first milestone!
                </div>
              ) : (
                achievements.map((achievement) => (
                  <div
                    key={achievement.id}
                    className="bg-white border border-zinc-200 p-6 flex items-start gap-6"
                    data-testid={`achievement-row-${achievement.id}`}
                  >
                    <div className="w-1 h-full bg-[#FF66C4]/20 shrink-0" />
                    <div className="flex-1">
                      <p className="font-accent text-xs font-bold uppercase tracking-widest text-[#FF66C4] mb-1">
                        {new Date(achievement.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                      </p>
                      <h3 className="font-serif text-lg font-bold">{achievement.title}</h3>
                      <p className="text-zinc-600 mt-1">{achievement.description}</p>
                      {achievement.link && (
                        <a 
                          href={achievement.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#FF66C4] text-sm hover:underline mt-2 inline-block"
                        >
                          View link →
                        </a>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditAchievementDialog(achievement)}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAchievement(achievement.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
